// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState } from 'react';
import { Typography } from '@mui/material';
import { Variant } from '@mui/material/styles/createTypography';
import {
  DataGrid,
  GridColDef,
  GridValidRowModel,
  GridRowIdGetter,
  GridFilterModel,
  GridCallbackDetails,
  GridSortModel,
  GridEventListener,
} from '@mui/x-data-grid';
import { TableWrapper } from './TableWrapper';
import { dataGridSx } from './dataGridSx';
import { withScrollableCell } from './withScrollableCell';

type DataTableProps<R extends GridValidRowModel> = {
  title: string;
  variant: Variant;
  columns: GridColDef<R>[];
  rows: R[];
  rowKey: GridRowIdGetter<R>;
  toolbar?: React.FC<{ selected: R[] }>;
  onRowClick?: GridEventListener<'rowClick'> | undefined;
  onFilterModelChange?: (model: GridFilterModel, details: GridCallbackDetails<'filter'>) => void;
  defaultSortModel?: GridSortModel;
  enableTableWrapper?: boolean;
};

export const DataTable = <R extends GridValidRowModel>({
  title,
  variant,
  columns,
  rows,
  rowKey,
  toolbar,
  onFilterModelChange,
  defaultSortModel,
  onRowClick,
  enableTableWrapper,
}: DataTableProps<R>): JSX.Element => {
  const [sortModel, setSortModel] = useState<GridSortModel>(defaultSortModel || []);

  // Use the withScrollableCell utility to wrap columns
  const scrollableColumns = withScrollableCell(columns);
  const table = (
    <DataGrid
      onRowClick={onRowClick}
      disableRowSelectionOnClick
      aria-label={title}
      sx={(theme) => dataGridSx(theme)}
      columns={scrollableColumns} // Use scrollable columns
      rows={rows}
      getRowId={rowKey}
      slots={{ toolbar }}
      onFilterModelChange={onFilterModelChange}
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      rowSelection={false}
    />
  );
  if (enableTableWrapper) {
    return (
      <>
        {title && (
          <Typography sx={{ mt: 2, mb: 2 }} color="primary" variant={variant}>
            {title}
          </Typography>
        )}
        <TableWrapper>{table}</TableWrapper>
      </>
    );
  }
  return (
    <>
      <Typography sx={{ mt: 2, mb: 2 }} color="primary" variant={variant}>
        {title}
      </Typography>
      {table}
    </>
  );
};
